/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import type { FinderPreference } from '@sportninja/common/features/profile/finderSettings/types'
import { font, paragraphMedium } from 'src/components/css'
import FinderPreferenceCard from './FinderPreferenceCard'

interface Props {
  preferences: FinderPreference[]
  onEdit: (preference: FinderPreference) => void
  onRemove: (preference: FinderPreference) => void
}

/**
 * Responsive grid of `FinderPreferenceCard`s; renders an empty-state message
 * when there are no preferences yet.
 */
const FinderPreferenceGrid: React.FC<Props> = ({
  preferences,
  onEdit,
  onRemove,
}) => {
  if (preferences.length === 0) {
    return (
      <div css={emptyStyle}>
        No preferences yet. Add one to be found as a spare.
      </div>
    )
  }
  return (
    <div css={gridStyle}>
      {preferences.map((preference) => (
        <FinderPreferenceCard
          key={preference.id}
          preference={preference}
          onEdit={onEdit}
          onRemove={onRemove}
        />
      ))}
    </div>
  )
}

export default FinderPreferenceGrid

// ─── Styles ────────────────────────────────────────────────────────────────

const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
`

const emptyStyle = css`
  ${paragraphMedium}
  ${font.body}
  color: ${colors.NEUTRAL_50};
  text-align: center;
  padding: 32px;
`
